import React, { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Tooltip } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { boolean, object } from 'yup';
import dayjs from 'dayjs';
import { type CheckList } from './types';
import { roleTextMap } from './constant';
import type {
  DsrcApproval,
  FrcAssessment,
  StudentAssessmentDetail,
  StudentInfo,
} from '../../../graphql';
import { FrcsbDecision } from '../../../FRCAssessment';
import { PAGE_TEXT } from '../../../lang';
import { PAGE_TEXT as FUNDING_TEXT } from '../../../../Nomination/components/lang';
import { AssessmentAccordion } from '../../../components';
import {
  AcademicQualificationForms,
  AcademicRefereeForms,
  DeclarationForm,
  getOptionsLabel,
  LanguageQualificationForm,
  OtherTestForm,
  SupervisorForms,
} from '../../../../Monitor/components';
import { Checkbox, Button, DisplayFileData } from '../../../../../components';
import {
  MeritAssessmentsContent,
  type MeritAssessmentsType,
  type MeritAssessmentsValue,
  PublicationAssessmentsContent,
  type PublicationAssessmentsType,
  type PublicationAssessmentsValue,
} from '../../../DRCAssessment';
import { handleReadySubmit } from '../../../../../utils';
import type {
  LanguageQualification,
  OtherTest,
  AcademicQualificationFormData,
} from '../../../../Application/graphql';
import { type AcademicRefereeData } from '../../../../Monitor/graphql';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';
import { dsrcApprovalDecisionOptions } from '../../../DRCApproval/components/options';

type GSCheckListBoxProps = {
  onSubmit: (values: CheckList) => void;
  hadGsChecked: boolean;
  returnedRemarks: ApplicationRemarkType[];
  studentAssessment: {
    studentAssessmentDetail: StudentAssessmentDetail;
    dsrcAssessment: {
      dsrcApproval: DsrcApproval;
      drcRequired: boolean;
    };
    meritAssessments: MeritAssessmentsType[];
    publicationAssessments: PublicationAssessmentsType[];
    frcAssessment: FrcAssessment;
  };
  studentData: {
    studentInfo: StudentInfo;
    studentAcademic: AcademicQualificationFormData[];
    studentLanguage: LanguageQualification;
    studentOtherTest?: OtherTest;
    studentAcademicReferees: AcademicRefereeData[];
  };
};

type DisplayData = {
  label: ReactNode;
  value?: ReactNode;
  reference?: ReactNode;
  formikName?: keyof CheckList;
  moreInfo?: ReactNode;
  checkRequired: boolean;
  enableExpand: boolean;
};

const proofDocHintArr = Object.values(
  FUNDING_TEXT.FUNDING_SOURCE.PROOF_OF_INCENTIVE_CATEGORIES.HINTS,
);

const proofDocTooltip: React.ReactNode = (
  <>
    {proofDocHintArr.map((item, index) => {
      return <div key={index}>{`${index + 1}.  ${item}`}</div>;
    })}
  </>
);

const STEP_TEXT = PAGE_TEXT.GS_ASSESSMENT_DETAILS.CHECK;
const { STIPEND, TUITION_FEE, ACCOUNT_CODE } =
  PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_G;
const DRC_TEXT = PAGE_TEXT.DSRC_APPROVAL_DETAILS.ASSESSMENT;

const FRC_TEXT = PAGE_TEXT.FRC_ASSESSMENT_DETAILS.ASSESSMENT;
const HOD_TEXT = PAGE_TEXT.HOD_DOS_ASSESSMENT;
const { SECTION_A, SECTION_B, SECTION_C } = STEP_TEXT;
const frcApprovalRmk = Object.values(SECTION_B.FRC_APPROVAL_REMARKS);

export const GSCheckListBox: React.FunctionComponent<
  GSCheckListBoxProps
> = props => {
  const { applicationId = '' } = useParams();
  const {
    onSubmit,
    hadGsChecked,
    returnedRemarks,
    studentAssessment: {
      publicationAssessments,
      meritAssessments,
      studentAssessmentDetail,
      frcAssessment,
      dsrcAssessment: { dsrcApproval, drcRequired },
    },
    studentData: {
      studentInfo,
      studentAcademic,
      studentLanguage,
      studentOtherTest,
      studentAcademicReferees,
    },
  } = props;

  // sort returnedRemarks by createdAt in ascending order.
  const sortedRemarks = [...returnedRemarks].sort((a, b) => {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const initialValues: CheckList = {
    visaTypeChecked: hadGsChecked,
    localIndicatorChecked: hadGsChecked,
    applicationDeclarationChecked: hadGsChecked,
    offerDeptTypeChecked: hadGsChecked,
    offerTypeChecked: hadGsChecked,
    programmeOfferedChecked: hadGsChecked,
    previousSuccessfulExperienceChecked: hadGsChecked,
    educationChecked: hadGsChecked,
    sourceOfFundingChecked: hadGsChecked,
    proofOfSchemeChecked: hadGsChecked,
    accountCodeOfFundingChecked: hadGsChecked,
    supervisorRemarksChecked: hadGsChecked,
    ULChecked: hadGsChecked,
    ALChecked: hadGsChecked,
    RLChecked: hadGsChecked,
    supervisorDeclarationChecked: hadGsChecked,
    justificationChecked: hadGsChecked,
    justificationForFourChecked: hadGsChecked,
    languageChecked: hadGsChecked,
    otherTestChecked: hadGsChecked,
    refereeReportsChecked: hadGsChecked,
    tuitionFeeChecked: hadGsChecked,
    stipendChecked: hadGsChecked,
    dorpgsRemarksChecked: hadGsChecked,
    dsrcApprovalChecked: hadGsChecked,
    frcApprovalChecked: hadGsChecked,
    hodApprovalChecked: hadGsChecked,
  };

  const {
    offerType,
    isFirmOffer,
    conditions,
    firstDegreeUL,
    firstDegreeAL,
    RL1Count,
    RL2Count,
    stipendApproval,
    tuitionAssistantshipType,
    tuitionFeeAccountCode,
    hodDosAssessment,
    conditionFulfillingDeadline,
    contactDepartmentPersonEmail,
    contactDepartmentPersonName,
    dorpgsRemarks,
  } = studentAssessmentDetail;

  const {
    applicationNo,
    applicantName,
    visaType,
    isLocal,
    doneApplicationDeclaration,
    identityDocumentType,
    department,
    programmeOffered,
    semester,
    supPreviousSuccessfulSupervision,
    fundingSource,
    successfulFundingPlan,
    proofOfScheme,
    declarations,
    conditionJustification,
    supervisors,
    programmeCode,
    justificationForFour,
    supervisorRemarkToDorpgs,
  } = studentInfo;

  const { approveDsrcChairInTeam, approveWaivingEntranceRequirements } =
    frcAssessment;

  const noFrcAssessment =
    approveDsrcChairInTeam === FrcsbDecision.NA &&
    approveWaivingEntranceRequirements === FrcsbDecision.NA;
  const noHoddosAssessment = hodDosAssessment === null;

  const publicationAssessmentsFormik = useFormik<PublicationAssessmentsValue>({
    initialValues: {
      publicationAssessments,
    },
    onSubmit: () => {},
  });

  const meritAssessmentsFormik = useFormik<MeritAssessmentsValue>({
    initialValues: {
      meritAssessments,
    },
    onSubmit: () => {},
  });

  const formik = useFormik<CheckList>({
    initialValues,
    validationSchema: object({
      visaTypeChecked: boolean().required().oneOf([true]),
      localIndicatorChecked: boolean().required().oneOf([true]),
      applicationDeclarationChecked: boolean().required().oneOf([true]),
      offerDeptTypeChecked: boolean().required().oneOf([true]),
      offerTypeChecked: boolean().required().oneOf([true]),
      programmeOfferedChecked: boolean().required().oneOf([true]),
      previousSuccessfulExperienceChecked: boolean().required().oneOf([true]),
      educationChecked: boolean().required().oneOf([true]),
      sourceOfFundingChecked: boolean().required().oneOf([true]),
      proofOfSchemeChecked: boolean().required().oneOf([true]),
      accountCodeOfFundingChecked: boolean().required().oneOf([true]),
      supervisorRemarksChecked: boolean().required().oneOf([true]),
      ULChecked: boolean().required().oneOf([true]),
      ALChecked: boolean().required().oneOf([true]),
      RLChecked: boolean().required().oneOf([true]),
      supervisorDeclarationChecked: boolean().required().oneOf([true]),
      justificationChecked: boolean().required().oneOf([true]),
      justificationForFourChecked: boolean().required().oneOf([true]),
      languageChecked: boolean().required().oneOf([true]),
      otherTestChecked: boolean().required().oneOf([true]),
      refereeReportsChecked: boolean().required().oneOf([true]),
      tuitionFeeChecked: boolean().required().oneOf([true]),
      stipendChecked: boolean().required().oneOf([true]),
      dsrcApprovalChecked: boolean().required().oneOf([true]),
      dorpgsRemarksChecked: boolean().required().oneOf([true]),
      frcApprovalChecked: boolean().required().oneOf([true]),
      hodApprovalChecked: boolean().required().oneOf([true]),
    }),
    onSubmit,
  });

  const displayData: DisplayData[] = [
    {
      label: SECTION_A.APPLICATION_NO,
      value: applicationNo,
      checkRequired: false,
      enableExpand: false,
    },
    {
      label: SECTION_A.APPLICATION_NAME,
      value: applicantName,
      checkRequired: false,
      enableExpand: false,
    },
    {
      label: SECTION_A.VISA_TYPE,
      value: visaType,
      formikName: 'visaTypeChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.LOCAL_INDICATOR,
      value: isLocal + identityDocumentType,
      reference: (
        <h2>
          {SECTION_A.LOCAL_REF1}&nbsp;
          <a
            href="https://www.jupas.edu.hk/en/page/detail/3670/#def_nls"
            target="_blank"
            rel="noopener noreferrer"
            className="udl"
          >
            {SECTION_A.LOCAL_REF_LINK}
          </a>
          {SECTION_A.LOCAL_REF2}
        </h2>
      ),
      formikName: 'localIndicatorChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.DECLARATION,
      value: doneApplicationDeclaration ? PAGE_TEXT.YES : PAGE_TEXT.NO,
      formikName: 'applicationDeclarationChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.OFFER_DEPT,
      value: department + ' (' + programmeCode + ')',
      formikName: 'offerDeptTypeChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.OFFER_TYPE,
      value: offerType,
      formikName: 'offerTypeChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.PROGRAMME_OFFERED,
      value: (
        <>
          {programmeOffered}
          {!isFirmOffer && (
            <h2 className="py-2 !font-medium">
              <div>
                <span className="underline !font-semibold">
                  {SECTION_A.CONDITIONS}:
                </span>{' '}
                {conditions.join(', ')}
              </div>
              <div>
                <span className="underline !font-semibold">
                  {SECTION_A.DEADLINE}:
                </span>{' '}
                {conditionFulfillingDeadline}
              </div>
              <div>
                <span className="underline !font-semibold">
                  {SECTION_A.CONTACT_PERSON}:
                </span>{' '}
                {contactDepartmentPersonName} - {contactDepartmentPersonEmail}
              </div>
            </h2>
          )}
        </>
      ),
      reference: (
        <h2>
          {SECTION_A.PROGRAMME_REF}&nbsp;
          <a
            href="http://www51.polyu.edu.hk/eprospectus/rpg/admission-requirements"
            target="_blank"
            rel="noopener noreferrer"
            className="udl"
          >
            {SECTION_A.PROGRAMME_LINK}
          </a>
        </h2>
      ),
      formikName: 'programmeOfferedChecked',
      checkRequired: true,
      enableExpand: false,
    },

    {
      label: SECTION_A.SEMESTER,
      value: semester,
      checkRequired: false, // no need to check
      enableExpand: false,
    },
    {
      label: SECTION_A.PREVIOUS_SUCCESSFUL,
      value: supPreviousSuccessfulSupervision,
      reference: (
        <h2>
          {SECTION_A.PREVIOUS_SUCCESSFUL_RMK}&nbsp;
          <a
            href={`/internal/monitor/${applicationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="udl"
          >
            {SECTION_A.PREVIOUS_SUCCESSFUL_LINK}
          </a>
          .
        </h2>
      ),
      formikName: 'previousSuccessfulExperienceChecked',
      checkRequired: true,
      enableExpand: false,
      moreInfo: (
        <AssessmentAccordion
          title={SECTION_A.PREVIOUS_SUCCESSFUL_LINK}
          size="sm"
          className="accordion-content"
          defaultExpanded
          AssessDetails={<SupervisorForms data={supervisors} />}
        />
      ),
    },
    {
      label: (
        <AssessmentAccordion
          title={SECTION_A.EDUCATION}
          size="sm"
          className="accordion-content"
          defaultExpanded
          AssessDetails={
            <AcademicQualificationForms data={studentAcademic} GSview={true} />
          }
        />
      ),
      formikName: 'educationChecked',
      checkRequired: true,
      enableExpand: true,
    },
    {
      label: (
        <AssessmentAccordion
          title={SECTION_A.LANGUAGE}
          className="accordion-content"
          size="sm"
          defaultExpanded
          AssessDetails={<LanguageQualificationForm data={studentLanguage} />}
        />
      ),

      formikName: 'languageChecked',
      checkRequired: true,
      enableExpand: true,
    },
    {
      label: (
        <AssessmentAccordion
          title={SECTION_A.OTHER_TEST}
          className="accordion-content !text-sm"
          size="sm"
          defaultExpanded
          AssessDetails={<OtherTestForm data={studentOtherTest} />}
        />
      ),

      formikName: 'otherTestChecked',
      checkRequired: true,
      enableExpand: true,
    },

    {
      label: (
        <AssessmentAccordion
          title={SECTION_A.REFEREE_REPORTS}
          className="accordion-content !text-sm"
          size="sm"
          defaultExpanded
          AssessDetails={
            <AcademicRefereeForms data={studentAcademicReferees} />
          }
        />
      ),

      formikName: 'refereeReportsChecked',
      checkRequired: true,
      enableExpand: true,
    },
    {
      label: SECTION_A.FUNDING,
      moreInfo: (
        <div className="mb-2">
          {fundingSource?.map((f, index) => (
            <div key={index} className="font-bold pl-6 pr-16 break-all">
              {f}
            </div>
          ))}
        </div>
      ),
      formikName: 'sourceOfFundingChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: (
        <Tooltip title={proofDocTooltip} arrow>
          <span>&nbsp;{SECTION_A.PROOF_DOC}</span>
        </Tooltip>
      ),
      value: <DisplayFileData fileData={proofOfScheme ?? []} />,
      formikName: 'proofOfSchemeChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: <span>&nbsp;{SECTION_A.FUNDING_ARRANGEMENT}</span>,
      value:
        successfulFundingPlan?.length === 0 ? (
          <>{'N/A'}</>
        ) : (
          <>
            {successfulFundingPlan.map((sfp, index) => {
              const { fundingLabel, plainFundingPlan } = sfp;
              return (
                <div key={index}>
                  <p>{fundingLabel}</p>
                  <p>{SECTION_A.ACCOUNT_CODE}:</p>
                  <div className="ml-[32px]">
                    {plainFundingPlan.map((plans, index) => {
                      const { accountCode, from, to } = plans;
                      return (
                        <span
                          key={index}
                          className={index === 0 ? '' : '!ml-28'}
                        >
                          {accountCode}
                          <span className="font-semibold">
                            &nbsp;{`(`} &nbsp;
                            {SECTION_A.FROM}
                            {from?.toString()}
                            {`, `}
                            {SECTION_A.TO}
                            {to?.toString()}&nbsp;{`)`}
                          </span>
                          <br />
                        </span>
                      );
                    })}
                  </div>
                  <hr className="my-2" />
                </div>
              );
            })}
          </>
        ),
      formikName: 'accountCodeOfFundingChecked',
      checkRequired: true,
      enableExpand: false,
    },
    {
      label: SECTION_A.CHIEF_SUP_REMARKS,
      moreInfo: (
        <div className="font-bold pl-6 pr-16 break-all">
          {supervisorRemarkToDorpgs}
        </div>
      ),
      formikName: 'supervisorRemarksChecked',
      checkRequired: true,
      enableExpand: false,
    },
  ];

  const isExternalDEARequired = hodDosAssessment?.externalGrantOrDEA.required;
  const isNonSTEMOrNonSHSRequired = hodDosAssessment?.nonSTEMOrNonSHS.required;
  const isDualPhDRequired = hodDosAssessment?.dualPhD.required;
  const isCompetitiveFundRequired = hodDosAssessment?.competitiveFund.required;
  type RequiredFundingKeys =
    | 'isExternalDEARequired'
    | 'isNonSTEMOrNonSHSRequired'
    | 'isDualPhDRequired'
    | 'isCompetitiveFundRequired';

  const hoddosRequiredContent: Record<
    RequiredFundingKeys,
    {
      title: string;
      content: React.ReactNode;
    }
  > = {
    isExternalDEARequired: {
      title: HOD_TEXT.ASSESSMENT.FOR_EXTERNAL.TITLE,
      content: (
        <>
          <p>{HOD_TEXT.ASSESSMENT.FOR_EXTERNAL.CONTENT}</p>
          <p className="mt-[1.25rem]">
            {ACCOUNT_CODE}:&nbsp;
            <b>{hodDosAssessment?.externalGrantOrDEA.accountCode}</b>
          </p>
        </>
      ),
    },
    isNonSTEMOrNonSHSRequired: {
      title: HOD_TEXT.ASSESSMENT.FOR_UNDER_STRATEGIC.TITLE,
      content: (
        <>
          <p>{HOD_TEXT.ASSESSMENT.FOR_UNDER_STRATEGIC.CONTENT_1}</p>
          <p className="pt-4">
            {HOD_TEXT.ASSESSMENT.FOR_UNDER_STRATEGIC.CONTENT_2}
            &nbsp;<b>{hodDosAssessment?.nonSTEMOrNonSHS.assumptionDate}</b>
          </p>
          <p className="pt-4">
            {ACCOUNT_CODE}:&nbsp;
            <b>{hodDosAssessment?.nonSTEMOrNonSHS.accountCode ?? 'N/A'}</b>
          </p>
        </>
      ),
    },
    isDualPhDRequired: {
      title: HOD_TEXT.ASSESSMENT.FOR_DUAL.TITLE,
      content: (
        <>
          <p>{HOD_TEXT.ASSESSMENT.FOR_DUAL.CONTENT}</p>
          <p className="mt-[1.25rem]">
            {ACCOUNT_CODE}&nbsp;<b>{hodDosAssessment?.dualPhD.accountCode}</b>
          </p>
        </>
      ),
    },
    isCompetitiveFundRequired: {
      title: HOD_TEXT.ASSESSMENT.FOR_COMPETITIVE.TITLE,
      content: (
        <>
          <p>{HOD_TEXT.ASSESSMENT.FOR_COMPETITIVE.CONTENT}</p>
          <p className="mt-[1.25rem]">
            {ACCOUNT_CODE}&nbsp;
            <b>{hodDosAssessment?.competitiveFund.accountCode}</b>
          </p>
        </>
      ),
    },
  };

  const getHoDDosRequiredContent = (): {
    title: React.ReactNode;
    content: React.ReactNode;
  } | null => {
    if (isExternalDEARequired)
      return hoddosRequiredContent.isExternalDEARequired;
    if (isNonSTEMOrNonSHSRequired)
      return hoddosRequiredContent.isNonSTEMOrNonSHSRequired;
    if (isDualPhDRequired) return hoddosRequiredContent.isDualPhDRequired;
    if (isCompetitiveFundRequired)
      return hoddosRequiredContent.isCompetitiveFundRequired;
    return null;
  };

  return (
    <form onSubmit={handleReadySubmit(formik)} noValidate>
      <ContentBox>
        <h2
          className={
            hadGsChecked ? 'ml-6 my-6 text-[#951D1D] font-semibold' : 'hidden'
          }
        >
          {STEP_TEXT.NOTE}
        </h2>

        <div className="mb-8">
          <AssessmentAccordion
            title={SECTION_A.TITLE}
            defaultExpanded={!hadGsChecked}
            AssessDetails={
              <div className="pl-6">
                <div>
                  {displayData.map((row, index) => {
                    const {
                      label,
                      value,
                      reference,
                      formikName,
                      checkRequired,
                      enableExpand,
                      moreInfo,
                    } = row;
                    return (
                      <div key={index} className="check-row">
                        <div className="flex justify-between items-start">
                          <div className="flex justify-start justify-self-start text-sm py-2">
                            {enableExpand ? ' ' : index + 1 + '. '}
                            {label}
                            {enableExpand ? '' : ': '}&nbsp;
                            <span className="font-bold text-sm">{value}</span>
                          </div>
                          {checkRequired && (
                            <Checkbox
                              label=""
                              disabled={hadGsChecked}
                              name={formikName}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={
                                formik.values[
                                  formikName as keyof typeof formik.values
                                ]
                              }
                            />
                          )}
                        </div>
                        {reference && (
                          <div className="italic text-[#951D1D] text-sm mb-4 px-4">
                            {reference}
                          </div>
                        )}
                        {moreInfo && <div>{moreInfo}</div>}
                      </div>
                    );
                  })}
                </div>

                <div className="justify-self-start font-semibold my-4 text-[#951D1D]">
                  {SECTION_A.SCHEME_ENTRANCE_REQ}
                </div>
                <div className="check-row pl-[24px]">
                  <div
                    className="grid grid-cols-3 place-content-between items-start"
                    style={{ gridTemplateColumns: '8fr 1fr' }}
                  >
                    <div className="justify-self-start mt-2 text-sm ml-6">
                      {SECTION_A.SCHEME_ENTRANCE_REQ_A1}
                      <b>{firstDegreeUL !== '' ? 'UL' + firstDegreeUL : ''}</b>
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="ULChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.ULChecked}
                    />
                    <div className="justify-self-start mt-2 ml-10 text-sm">
                      {SECTION_A.SCHEME_ENTRANCE_REQ_A2}{' '}
                      <b>{firstDegreeAL !== '' ? 'AL' + firstDegreeAL : ''}</b>
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="ALChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.ALChecked}
                    />
                  </div>
                </div>

                <div className={`flex justify-between items-start check-row`}>
                  <AssessmentAccordion
                    title={SECTION_A.SCHEME_ENTRANCE_REQ_B}
                    titleBold={`RL:${RL1Count}, RL2:${RL2Count} `}
                    size="sm"
                    defaultExpanded
                    className="accordion-content !text-sm"
                    AssessDetails={
                      <div className="ml-10">
                        {publicationAssessments.length > 0 ? (
                          <div>
                            {SECTION_A.PUBLICATION}
                            <FormikProvider
                              value={publicationAssessmentsFormik}
                            >
                              {publicationAssessments.map(
                                (_, index: number) => {
                                  return (
                                    <PublicationAssessmentsContent
                                      key={index}
                                      index={index}
                                      isViewMode={true}
                                    />
                                  );
                                },
                              )}
                            </FormikProvider>
                          </div>
                        ) : (
                          <div>
                            {SECTION_A.PUBLICATION}
                            <b>&nbsp;N/A</b>
                          </div>
                        )}
                        {meritAssessments.length > 0 ? (
                          <div className="pt-4">
                            {SECTION_A.MERIT}
                            <FormikProvider value={meritAssessmentsFormik}>
                              {meritAssessments.map((_, index: number) => (
                                <MeritAssessmentsContent
                                  key={index}
                                  index={index}
                                  isViewMode={true}
                                />
                              ))}
                            </FormikProvider>
                          </div>
                        ) : (
                          <div className="pt-4">
                            {SECTION_A.MERIT}
                            <b>&nbsp;N/A</b>
                          </div>
                        )}
                      </div>
                    }
                  />
                  <Checkbox
                    className="justify-self-end"
                    label=""
                    disabled={hadGsChecked}
                    name="RLChecked"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.RLChecked}
                  />
                </div>
              </div>
            }
          />
          <AssessmentAccordion
            title={SECTION_B.TITLE}
            defaultExpanded={!hadGsChecked}
            AssessDetails={
              <div className="pl-6 text-sm">
                {/* 1. Supervisors Declaration */}
                <div className={`flex justify-between items-start check-row`}>
                  <div>
                    <div className={`text-sm`}>{SECTION_B.DECLARATION}</div>
                    <DeclarationForm
                      data={declarations}
                      supervisorsData={supervisors}
                      grayCheckbox
                    />
                  </div>
                  <Checkbox
                    className="justify-self-end"
                    label=""
                    disabled={hadGsChecked}
                    name="supervisorDeclarationChecked"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.supervisorDeclarationChecked}
                  />
                </div>

                {/* 2. justification for admission to 4-year */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className={`justify-self-start text-sm`}>
                      {SECTION_B.JUSTIFICATION_FOUR_YEAR}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="justificationForFourChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.justificationForFourChecked}
                    />
                  </div>
                  <h2 className="ml-4 italic mt-2 text-[#224295]">
                    {SECTION_B.SUP}
                  </h2>
                  <h2 className="px-12 mt-2">
                    {justificationForFour === '' ? '-' : justificationForFour}
                  </h2>
                </div>
                {/* 3. Justification  */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.CONDITION_JUSTIFICATION}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="justificationChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.justificationChecked}
                    />
                  </div>
                  <h2 className="ml-4 italic my-4 text-[#224295]">
                    {SECTION_B.SUP}
                  </h2>
                  <h2 className="px-12 mt-2">
                    {conditionJustification === ''
                      ? '-'
                      : conditionJustification}
                  </h2>
                </div>
                {/* 4. Tuition Fee  */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.TUITION_FEE}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="tuitionFeeChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.tuitionFeeChecked}
                    />
                  </div>
                  <>
                    <h2 className="ml-4 italic my-4 text-[#224295]">
                      {SECTION_B.DORPGS}
                    </h2>
                    <div className="px-12 mt-2">
                      {TUITION_FEE.PROVIDE_1}&nbsp;
                      <b>{tuitionAssistantshipType}</b>
                      &nbsp;{TUITION_FEE.PROVIDE_2}
                    </div>
                    <div className="px-12 mt-2">
                      {ACCOUNT_CODE}:&nbsp;<b>{tuitionFeeAccountCode}</b>
                    </div>
                  </>
                </div>
                {/* 5. Stipend  */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.STIPEND}
                      <span className="italic text-[#951D1D]">
                        {SECTION_B.STIPEND_REMARK}
                      </span>{' '}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="stipendChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.stipendChecked}
                    />
                  </div>
                  <h2 className="ml-4 italic mt-4 text-[#224295]">
                    {SECTION_B.DORPGS}
                  </h2>
                  <div className="px-12 mt-2">
                    {stipendApproval ? (
                      <b>{STIPEND.OPTION_1}&nbsp;</b>
                    ) : (
                      <b>{STIPEND.OPTION_2}</b>
                    )}
                  </div>
                </div>

                {/* 6. DoRPgS Remarks */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.DORPGS_REMARKS}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="dorpgsRemarksChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.dorpgsRemarksChecked}
                    />
                  </div>
                  <h2 className="ml-4 mt-4 italic text-[#224295]">
                    {SECTION_B.DORPGS}
                  </h2>
                  <div className="px-12 mt-2">
                    {dorpgsRemarks === '' ? (
                      <h2>{'-'}</h2>
                    ) : (
                      <div>
                        <div className="mb-2">
                          <b>{dorpgsRemarks}</b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* 7. D/SRC’s Approval */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.DSRC_APPROVAL}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="dsrcApprovalChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.dsrcApprovalChecked}
                    />
                  </div>
                  <h2 className="ml-4 mt-4 italic text-[#224295]">
                    {SECTION_B.DRC}
                  </h2>
                  <div className="px-12 mt-2">
                    {!drcRequired ? (
                      <h2>{'-'}</h2>
                    ) : (
                      <div>
                        <div className="mb-2">
                          {DRC_TEXT.DRC_CHAIR.TITLE}
                          <div className="mb-2">
                            {DRC_TEXT.DRC_CHAIR.OPTION_0}
                            <b>
                              {getOptionsLabel(
                                dsrcApprovalDecisionOptions,
                                dsrcApproval.approveDoRPgSInTeam,
                              )}
                            </b>
                            {DRC_TEXT.DRC_CHAIR.OPTION_1}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* 8.HoD/DoS’s Approval */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.HOD_DOS_APPROVAL}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="hodApprovalChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.hodApprovalChecked}
                    />
                  </div>
                  <h2 className="ml-4 mt-4 italic text-[#224295]">
                    {SECTION_B.HOD}
                  </h2>
                  <div className="px-12 mt-2">
                    {noHoddosAssessment ? (
                      <h2>{'-'}</h2>
                    ) : (
                      <div>
                        <div className="mb-4">
                          {getHoDDosRequiredContent()?.title}
                          &nbsp;
                          <b>{hodDosAssessment.decision.toLocaleLowerCase()}</b>
                          .
                        </div>
                        {getHoDDosRequiredContent()?.content}
                      </div>
                    )}
                  </div>
                </div>
                {/* 9.FRC/SB’s Approval */}
                <div className="check-row pb-2">
                  <div className={`flex justify-between items-start`}>
                    <div className="justify-self-start mt-2">
                      {SECTION_B.FRC_APPROVAL}
                    </div>
                    <Checkbox
                      className="justify-self-end"
                      label=""
                      disabled={hadGsChecked}
                      name="frcApprovalChecked"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.frcApprovalChecked}
                    />
                  </div>

                  <div className="ml-6 italic text-[#951D1D]">
                    {frcApprovalRmk.map((rmk, index) => (
                      <h2 key={index}>{rmk}</h2>
                    ))}
                  </div>
                  <h2 className="ml-4 italic mt-4 text-[#224295]">
                    {SECTION_B.FRC}
                  </h2>
                  <div className="px-12 mt-2">
                    {noFrcAssessment ? (
                      <h2>{'-'}</h2>
                    ) : (
                      <div>
                        {approveDsrcChairInTeam !== FrcsbDecision.NA && (
                          <>
                            <h2 className="pb-2">
                              {FRC_TEXT.DRC_CHAIR.TITLE},
                            </h2>
                            <h2>
                              {FRC_TEXT.DRC_CHAIR.OPTION_0}
                              <b>
                                {approveDsrcChairInTeam?.toLocaleLowerCase()}
                              </b>
                              {FRC_TEXT.DRC_CHAIR.OPTION_1}
                            </h2>
                          </>
                        )}
                        {approveWaivingEntranceRequirements !==
                          FrcsbDecision.NA && (
                          <>
                            <h2 className="py-2">
                              {FRC_TEXT.REQUIREMENTS.TITLE},
                            </h2>
                            <h2>
                              {FRC_TEXT.REQUIREMENTS.OPTION_0}
                              <b>
                                {approveWaivingEntranceRequirements?.toLocaleLowerCase()}
                              </b>
                              {FRC_TEXT.REQUIREMENTS.OPTION_1}
                            </h2>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            }
          />

          {sortedRemarks.length > 0 && (
            <AssessmentAccordion
              title={SECTION_C.TITLE}
              defaultExpanded={!hadGsChecked}
              AssessDetails={
                <div className="px-6">
                  <h2 className="mb-4 pl-2 text-[#951d1d] italic">
                    {SECTION_C.NOTE}
                  </h2>
                  <div className="pl-2">
                    {sortedRemarks.map((rmk, index) => {
                      const { createdAt, toRole, fromRole, remark } = rmk;
                      const createdTime =
                        dayjs(createdAt).format('DD/MM/YYYY HH:mm');
                      const toRoles = toRole
                        .map(role => roleTextMap[role])
                        .join(', ');
                      const frmRole = roleTextMap[fromRole];

                      return (
                        <div key={index}>
                          {index + 1}. {frmRole} to {toRoles}
                          <i>&nbsp;({createdTime}):</i>
                          <div className="pl-4 font-bold py-2">{remark}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              }
            />
          )}
          {!hadGsChecked && (
            <Box
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              marginY={4}
            >
              <Button variant="contained" type="submit">
                {STEP_TEXT.SAVE}
              </Button>
            </Box>
          )}
        </div>
      </ContentBox>
    </form>
  );
};

const ContentBox = styled.div`
  margin-top: 1rem;
  .text-udl {
    text-decoration: underline;
    color: #373fff;
    margin-top: 8px;
  }

  .check-row {
    transition: background-color;
  }

  .check-row:hover {
    background-color: #f6f6f6;
  }

  .check-row:hover .accordion-content {
    background-color: #f6f6f6;
  }

  .udl {
    text-decoration: underline;
  }
`;
